import { RefObject, useCallback, useEffect, useState } from "react";
import { useAppSelector } from "../../app";

const MIN_VALUE_PERCENTAGE = 39;
const MAX_VALUE_PERCENTAGE = 61;

// The scroll metrics read off the resolved `.ant-table-body` element. Declared
// as `Partial` because the code below defensively guards against the
// properties being absent, which a plain `Element` type would rule out.
type ScrollMetrics = Partial<
  Pick<Element, "scrollLeft" | "scrollWidth" | "clientWidth">
>;

const useHorizontalScrollIndicator = (
  refTableContainer: RefObject<HTMLDivElement | null>,
  data: unknown[] = [],
) => {
  const [scrollPercentage, setScrollPercentage] =
    useState<number>(MIN_VALUE_PERCENTAGE);
  const [hasScroll, setHasScroll] = useState(false);

  const { triggerHasHorizontalScroll } = useAppSelector(
    (state) => state.documentSetsReducer,
  );

  const calculateScrollPercentage = useCallback(
    (tableBody: ScrollMetrics) => {
      const { scrollLeft, scrollWidth, clientWidth } = tableBody;

      if (
        scrollLeft === undefined ||
        scrollWidth === undefined ||
        clientWidth === undefined
      ) {
        return;
      }

      const hasHorizontalScroll = clientWidth !== scrollWidth;

      if (hasHorizontalScroll !== hasScroll) {
        setHasScroll(hasHorizontalScroll);
      }

      if (!hasHorizontalScroll) {
        return;
      }

      const maxScrollLeft = scrollWidth - clientWidth;
      let scrollPercent = (scrollLeft / maxScrollLeft) * 100;
      scrollPercent =
        MIN_VALUE_PERCENTAGE +
        (scrollPercent * (MAX_VALUE_PERCENTAGE - MIN_VALUE_PERCENTAGE)) / 100;
      setScrollPercentage(scrollPercent);
    },
    [hasScroll],
  );

  useEffect(() => {
    const tableBody =
      refTableContainer.current?.querySelector(".ant-table-body");

    if (!tableBody) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect -- hasScroll/scrollPercentage are measured off the live DOM (scrollLeft/scrollWidth/clientWidth of the rendered .ant-table-body) and so cannot be derived during render; measuring after commit and storing the result in state is the intended pattern here. The write is already guarded against redundant updates.
    calculateScrollPercentage(tableBody);
  }, [
    refTableContainer,
    data,
    triggerHasHorizontalScroll,
    calculateScrollPercentage,
  ]);

  useEffect(() => {
    const tableBody =
      refTableContainer.current?.querySelector(".ant-table-body");

    if (!tableBody) {
      return;
    }

    const handleResize = () => {
      calculateScrollPercentage(tableBody);
    };

    tableBody.addEventListener("scroll", () =>
      calculateScrollPercentage(tableBody),
    );
    window.addEventListener("resize", handleResize);

    return () => {
      tableBody.removeEventListener("scroll", () =>
        calculateScrollPercentage(tableBody),
      );
      window.removeEventListener("resize", handleResize);
    };
  }, [
    triggerHasHorizontalScroll,
    refTableContainer,
    calculateScrollPercentage,
  ]);

  return {
    scrollPercentage,
    hasScroll,
  };
};

export default useHorizontalScrollIndicator;
